import { useState, useCallback, useMemo } from 'react'
import { RiMusic2Line } from '@remixicon/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	closestCenter,
	type DragStartEvent,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	SortableContext,
	verticalListSortingStrategy,
	arrayMove,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { songsQueryOptions, reorderSongsMutationOptions } from '@/lib/queries/songs'
import { playMutationOptions } from '@/lib/queries/playback'
import { playlistQueryOptions } from '@/lib/queries/playlists'
import { SongSearch } from './song-search'
import { SongRowContent, type Song } from './song-row-content'
import { SortableSongRow } from './sortable-song-row'

export type { Song }

export function PlaylistTracklist({
	songs,
	playlistId,
	accentColor,
	currentIndex,
	currentSongId: currentSongIdProp,
	isPlaying,
}: {
	songs: Song[]
	playlistId: string
	accentColor: string
	currentIndex: number
	currentSongId?: string
	isPlaying: boolean
}) {
	const songCount = songs.length
	const queryClient = useQueryClient()
	const [activeId, setActiveId] = useState<string | null>(null)

	const reorderMutation = useMutation(reorderSongsMutationOptions(queryClient, playlistId))
	const playMutation = useMutation(playMutationOptions())

	// Use the songId from playback state when available, fall back to index-based lookup
	const currentSongId = isPlaying && songCount > 0
		? currentSongIdProp ?? songs[Math.min(currentIndex, songCount - 1)]?.id
		: null

	// Rotate the display so the current song is at the top (only during playback)
	const displaySongs = useMemo(() => {
		if (!isPlaying || songCount === 0 || !currentSongId) return songs
		const idx = songs.findIndex((s) => s.id === currentSongId)
		if (idx <= 0) return songs
		return [...songs.slice(idx), ...songs.slice(0, idx)]
	}, [songs, currentSongId, songCount, isPlaying])

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveId(event.active.id as string)
	}, [])

	// Apply the same rotation to the cache data, then arrayMove, then commit
	const reorderDisplaySongs = useCallback(
		(oldIndex: number, newIndex: number) => {
			const cached = queryClient.getQueryData(songsQueryOptions(playlistId).queryKey)
			if (!cached) return

			// Apply the same rotation to the cached data (use currentSongId to find rotation point)
			const currentIdx = currentSongId ? cached.findIndex((s) => s.id === currentSongId) : -1
			const isRotated = isPlaying && currentIdx > 0
			const rotatedCache = isRotated
				? [...cached.slice(currentIdx), ...cached.slice(0, currentIdx)]
				: cached

			const reordered = arrayMove(rotatedCache, oldIndex, newIndex)

			queryClient.setQueryData(songsQueryOptions(playlistId).queryKey, reordered)

			// If rotated, the rotation is now baked in — reset currentIndex to 0
			if (isRotated) {
				const playlistData = queryClient.getQueryData(playlistQueryOptions(playlistId).queryKey)
				if (playlistData) {
					queryClient.setQueryData(playlistQueryOptions(playlistId).queryKey, {
						...playlistData,
						currentIndex: 0,
					})
				}
			}

			reorderMutation.mutate(reordered.map((s) => s.id))
		},
		[queryClient, playlistId, isPlaying, currentSongId, reorderMutation]
	)

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) {
				setActiveId(null)
				return
			}

			// Prevent any swap involving the currently playing song
			if (currentSongId && (active.id === currentSongId || over.id === currentSongId)) {
				setActiveId(null)
				return
			}

			const oldIndex = displaySongs.findIndex((s) => s.id === active.id)
			const newIndex = displaySongs.findIndex((s) => s.id === over.id)
			if (oldIndex === -1 || newIndex === -1) {
				setActiveId(null)
				return
			}

			setActiveId(null)
			reorderDisplaySongs(oldIndex, newIndex)
		},
		[displaySongs, reorderDisplaySongs, currentSongId]
	)

	const handleDragCancel = useCallback(() => {
		setActiveId(null)
	}, [])

	const activeSong = activeId ? displaySongs.find((s) => s.id === activeId) : null

	const handlePlaySong = useCallback((songIndex: number) => {
		playMutation.mutate({ playlistId, startIndex: songIndex })
	}, [playMutation, playlistId])

	return (
		<section>
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
					Tracklist
				</h3>
				{songCount > 0 && (
					<span className="text-[11px] tabular-nums text-muted-foreground/50">
						{songCount} {songCount === 1 ? 'track' : 'tracks'}
					</span>
				)}
			</div>

			<SongSearch playlistId={playlistId} songCount={songCount} accentColor={accentColor} />

			{songCount === 0 ? (
				<div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/30 py-16">
					<div
						className="flex size-12 items-center justify-center rounded-full"
						style={{
							backgroundColor: `color-mix(in oklch, ${accentColor} 8%, transparent)`,
						}}
					>
						<RiMusic2Line className="size-5" style={{ color: `color-mix(in oklch, ${accentColor} 40%, transparent)` }} />
					</div>
					<p className="mt-4 text-sm text-muted-foreground/60">No songs yet</p>
					<p className="mt-1 text-[11px] text-muted-foreground/30">
						Add songs to this playlist to get started
					</p>
				</div>
			) : (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
				>
					<div className="max-h-[570px] overflow-y-auto">
						{/* Render current song statically, outside SortableContext */}
						{currentSongId && displaySongs[0]?.id === currentSongId && (
							<SongRowContent
								song={displaySongs[0]}
								index={songs.indexOf(displaySongs[0])}
								accentColor={accentColor}
								isCurrent
								onPlay={() => handlePlaySong(songs.indexOf(displaySongs[0]))}
							/>
						)}
						<SortableContext
							items={displaySongs.filter((s) => s.id !== currentSongId).map((s) => s.id)}
							strategy={verticalListSortingStrategy}
						>
							{displaySongs.map((song) => {
								if (song.id === currentSongId) return null
								const originalIndex = songs.indexOf(song)
								const displayIndex = displaySongs.indexOf(song)
								return (
									<SortableSongRow
										key={song.id}
										song={song}
										index={originalIndex}
										playlistId={playlistId}
										accentColor={accentColor}
										isCurrent={false}
										disabled={false}
										onPlay={() => handlePlaySong(originalIndex)}
										onMoveToTop={() => {
											if (displayIndex <= (currentSongId ? 1 : 0)) return
											reorderDisplaySongs(displayIndex, currentSongId ? 1 : 0)
										}}
									/>
								)
							})}
						</SortableContext>
					</div>
					<DragOverlay dropAnimation={null}>
						{activeSong ? (
							<SongRowContent
								song={activeSong}
								index={songs.indexOf(activeSong)}
								accentColor={accentColor}
								isDragOverlay
							/>
						) : null}
					</DragOverlay>
				</DndContext>
			)}
		</section>
	)
}
